import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";

const PAGE_SIZE = 25;
const emptyState = () => ({ items: [], loading: false, error: null, loaded: false });

// Commit one complete snapshot, never a mixture of old and newly fetched pages.
export const loadContentPages = async (client, query, field, isCurrent) => {
    const items = [];
    const ids = new Set();
    let expectedTotal;
    let expectedLastPage;

    for (let page = 1; ; page += 1) {
        if (!isCurrent()) return null;
        const result = await client.query({
            query,
            variables: { first: PAGE_SIZE, page },
            fetchPolicy: "no-cache",
        });
        if (!isCurrent()) return null;
        const connection = result.data && result.data[field];
        const info = connection && connection.paginatorInfo;
        if ((result.errors && result.errors.length) || !connection || !Array.isArray(connection.data) ||
            !info || info.currentPage !== page || !Number.isInteger(info.total) || info.total < 0 ||
            !Number.isInteger(info.lastPage) || info.lastPage < 1 ||
            typeof info.hasMorePages !== "boolean") {
            throw new Error("Invalid content page");
        }

        if (page === 1) {
            expectedTotal = info.total;
            expectedLastPage = info.lastPage;
        }
        if (info.total !== expectedTotal || info.lastPage !== expectedLastPage ||
            info.hasMorePages !== (page < expectedLastPage) ||
            connection.data.length > PAGE_SIZE ||
            (info.hasMorePages && connection.data.length !== PAGE_SIZE)) {
            throw new Error("Content pagination changed during loading");
        }

        connection.data.forEach(item => {
            if (!item || item.id === null || item.id === undefined || String(item.id) === "" || ids.has(String(item.id))) {
                throw new Error("Missing or repeated content ID");
            }
            ids.add(String(item.id));
            items.push(item);
        });
        if (items.length > expectedTotal) throw new Error("Inconsistent content total");
        if (!info.hasMorePages) {
            if (items.length !== expectedTotal) throw new Error("Incomplete content list");
            return items;
        }
    }
};

const usePaginatedAppContent = (query, field, isSessionCurrent) => {
    const client = useApolloClient();
    // Apollo 2 deduplicates by document identity without considering auth headers.
    // A document owned by this mount keeps a new session off an old in-flight read.
    const sessionQuery = useMemo(() => ({ ...query }), [query]);
    const [state, setState] = useState(emptyState);
    const mounted = useRef(true);
    const generation = useRef(0);
    const inFlight = useRef(null);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            generation.current += 1;
            inFlight.current = null;
        };
    }, [client, sessionQuery, field, isSessionCurrent]);

    const refresh = useCallback(() => {
        if (!mounted.current || !isSessionCurrent()) return Promise.resolve();
        if (inFlight.current) return inFlight.current;

        const requestGeneration = ++generation.current;
        const isCurrent = () => mounted.current && generation.current === requestGeneration && isSessionCurrent();
        setState(previous => ({ ...previous, loading: true, error: null }));

        const request = loadContentPages(client, sessionQuery, field, isCurrent)
            .then(items => {
                if (items !== null && isCurrent()) {
                    setState({ items, loading: false, error: null, loaded: true });
                }
            })
            .catch(error => {
                if (isCurrent()) setState(previous => ({ ...previous, loading: false, error }));
            })
            .finally(() => {
                if (inFlight.current === request) inFlight.current = null;
            });

        inFlight.current = request;
        return request;
    }, [client, sessionQuery, field, isSessionCurrent]);

    return { ...state, refresh };
};

export default usePaginatedAppContent;
